function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React from 'react';
import { array, arrayOf, bool, element, exact, func, node, number, object, oneOfType, shape, string } from 'prop-types';
import clsx from 'clsx';
import { toolbarClasses } from '@mui/material/Toolbar';
import { styled } from '@mui/material/styles';
import SovosMenuItem from '../../../sovos-menu-item';
import SovosToolbar from '../../../sovos-toolbar';
import SovosToolbarActions from '../../../sovos-toolbar-actions';
import SovosToolbarBulkActions from '../../../sovos-toolbar-bulk-actions';
import SovosToolbarDataGrouping from '../../../sovos-toolbar-data-grouping';
import SovosToolbarSearch from '../../../sovos-toolbar-search';
import SovosToolbarTitle from '../../../sovos-toolbar-title';
import SovosIconButton from '../../../sovos-icon-button';
import SovosIconMenu from '../../../sovos-icon-menu';
import QuickFilters, { quickFilterPropTypes } from './QuickFilters';
import Filters, { filterPropTypes } from './Filters';
import Columns from './Columns';
const StyledToolbar = styled(SovosToolbar)(_ref => {
  let {
    theme: {
      palette
    }
  } = _ref;
  return {
    ["&.".concat(toolbarClasses.root)]: {
      zIndex: 40,
      // shadow needs to be above table fixed header

      '&--fixedHeader': {
        boxShadow: 'none',
        borderBottom: "1px solid ".concat(palette.divider)
      }
    }
  };
});
const Toolbar = _ref2 => {
  let {
    actionIconButtons,
    bulkActions,
    bulkActionsCount,
    children,
    className: toolbarClassName,
    disabled = false,
    DataGroupingProps,
    filters,
    hasFixedHeader = false,
    labels,
    onClearSelection = () => {},
    quickFilters,
    search,
    setVisibleColumns,
    tableColumns,
    toolbarTitle
  } = _ref2;
  const handleSearch = value => {
    search.onSearch(value);
    onClearSelection();
  };
  const hasMenu = bulkAction => Array.isArray(bulkAction.onClick);
  let toolbarChildren = [];
  if (children) {
    toolbarChildren = children;
  } else {
    if (bulkActions) {
      toolbarChildren.push( /*#__PURE__*/React.createElement(SovosToolbarBulkActions, {
        count: bulkActionsCount,
        key: "bulkActions",
        selectedText: labels.bulkActions
      }, bulkActions.map(action => {
        const {
          className,
          icon,
          ...otherProps
        } = action;
        if (hasMenu(action)) {
          return /*#__PURE__*/React.createElement(SovosIconMenu, _extends({
            className: className,
            disabled: disabled,
            iconElement: icon,
            key: action.tooltipText,
            toolTipText: action.tooltipText
          }, otherProps), action.onClick.map(item => /*#__PURE__*/React.createElement(SovosMenuItem, {
            key: item.text,
            onClick: () => item.onClick()
          }, item.text)));
        }
        return /*#__PURE__*/React.createElement(SovosIconButton, _extends({
          className: className,
          disabled: disabled,
          key: action.tooltipText
        }, otherProps), icon);
      })));
    }
    if (toolbarTitle) {
      toolbarChildren.push( /*#__PURE__*/React.createElement(SovosToolbarTitle, {
        key: "title"
      }, toolbarTitle));
    }
    if (DataGroupingProps) {
      toolbarChildren.push( /*#__PURE__*/React.createElement(SovosToolbarDataGrouping, _extends({
        disabled: disabled,
        key: "dataGrouping"
      }, DataGroupingProps)));
    }
    if (quickFilters) {
      toolbarChildren.push( /*#__PURE__*/React.createElement(QuickFilters, {
        disabled: disabled,
        key: "quickFilters",
        quickFilters: quickFilters,
        onClearSelection: onClearSelection
      }));
    }
    if (search || filters || setVisibleColumns || actionIconButtons) {
      const actionButtons = [];
      if (search) {
        actionButtons.push( /*#__PURE__*/React.createElement(SovosToolbarSearch, {
          defaultValue: search.term,
          disabled: disabled,
          key: "search",
          onSearch: handleSearch,
          placeholder: labels.searchPlaceholder,
          searchTooltipText: labels.searchButtonTooltip
        }));
      }
      if (actionIconButtons) {
        actionIconButtons.forEach((action, i) => {
          const {
            icon,
            ...iconButtonProps
          } = action;
          actionButtons.push( /*#__PURE__*/React.createElement(SovosIconButton, _extends({
            disabled: disabled
          }, iconButtonProps, {
            key: action.tooltipText || i
          }), icon));
        });
      }
      if (setVisibleColumns) {
        actionButtons.push( /*#__PURE__*/React.createElement(Columns, {
          buttonTooltipText: labels.columnButtonTooltip,
          disabled: disabled,
          labels: labels.columnDrawer,
          key: "columns",
          setVisibleColumns: setVisibleColumns,
          tableColumns: tableColumns
        }));
      }
      if (filters) {
        actionButtons.push( /*#__PURE__*/React.createElement(Filters, {
          buttonTooltipText: labels.filterButtonTooltip,
          disabled: disabled,
          filters: filters,
          key: "filters",
          labels: labels.filterDrawer,
          onClearSelection: onClearSelection
        }));
      }
      toolbarChildren.push( /*#__PURE__*/React.createElement(SovosToolbarActions, {
        key: "actions"
      }, actionButtons));
    }
  }
  return /*#__PURE__*/React.createElement(StyledToolbar, {
    className: clsx({
      ["".concat(toolbarClasses.root, "--fixedHeader")]: hasFixedHeader
    }, toolbarClassName)
  }, toolbarChildren);
};
export const searchPropTypes = exact({
  term: string,
  onSearch: func.isRequired
});
export const actionIconButtonPropTypes = arrayOf(exact({
  icon: element.isRequired,
  onClick: oneOfType([func, arrayOf(exact({
    text: node.isRequired,
    onClick: func.isRequired
  }))]).isRequired,
  tooltipText: string.isRequired
}));
export const bulkActionsPropTypes = arrayOf(shape({
  icon: element.isRequired,
  onClick: oneOfType([func, arrayOf(exact({
    text: node.isRequired,
    onClick: func.isRequired
  }))]).isRequired,
  tooltipText: string.isRequired
}));
Toolbar.propTypes = {
  actionIconButtons: actionIconButtonPropTypes,
  bulkActions: bulkActionsPropTypes,
  bulkActionsCount: number,
  children: arrayOf(node),
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  disabled: bool,
  DataGroupingProps: object,
  filters: filterPropTypes,
  hasFixedHeader: bool,
  labels: object,
  onClearSelection: func,
  quickFilters: quickFilterPropTypes,
  search: searchPropTypes,
  setVisibleColumns: func,
  tableColumns: array,
  toolbarTitle: node
};
export default Toolbar;